import { Graph, INode } from '@antv/g6';
import trim from 'lodash/trim';

import { colors } from 'ui/theme';
import { getTextBackgroundName, highlightMatchingText, isTextShape } from '../shapes';
import { Shape } from '../types';
import { focusNode, FocusPosition } from './orientation';

export const HIGHLIGHTED_SHAPES_GRAPH_KEY = 'highlightedShapes';

export const searchColors = {
  focused: colors.tangerine100,
  default: colors.lemon100,
};

/** Returns all shapes whose text contains the value */
const getTextShapesWithValue = (node: INode, value: string) =>
  node.getContainer().findAll((shape) => {
    if (!isTextShape(shape)) return false;
    const text = shape?.get('attrs')?.text;
    return typeof text === 'string' ? text.toLowerCase().includes(value.toLowerCase()) : false;
  });

/** Highlights all text matching the string in a given node */
const highlightTextInNode = (node: INode, value: string) => {
  const shapes = getTextShapesWithValue(node, value) as Shape[];

  if (!shapes.length) return [];

  const group = node.getContainer();
  const nodeId = node.getID();

  return shapes.reduce((acc: Shape[], shape) => {
    const hs = highlightMatchingText({
      shape,
      value,
      group,
      namePrefix: `${getTextBackgroundName({ shape })}-node${nodeId}`,
    });
    // make sure the text is in front of the background
    shape.toFront();
    shape.getParent().toFront();
    return [...acc, ...hs];
  }, []);
};

/** Removes all the highlights */
const removeHighlights = (graph: Graph) => {
  const prevHighlightedShapes: Shape[] | undefined = graph.get(HIGHLIGHTED_SHAPES_GRAPH_KEY);
  if (prevHighlightedShapes) {
    prevHighlightedShapes.forEach((shape) => {
      shape.remove();
      shape.destroy();
    });
    graph.set(HIGHLIGHTED_SHAPES_GRAPH_KEY, []);
  }
};

const highlightBackground = (s?: Shape) => s && s.attr('fill', searchColors.focused);
const unHighlightBackground = (s?: Shape) => s && s.attr('fill', searchColors.default);

/** Highlight newly focused shape & unhighlight previously focused shape */
const updateBackgroundColors = (shapes: Shape[], previousIndex = 0, newIndex = 0) => {
  unHighlightBackground(shapes[previousIndex]);
  highlightBackground(shapes[newIndex]);
};

type ShapeNodeMap = { [key: string]: string };

const createShapeMap = (node: INode, shapes: Shape[]): ShapeNodeMap => {
  if (!shapes.length) return {};

  const nodeId = node.getID();

  return shapes.reduce(
    (mapAcc: ShapeNodeMap, shape) => ({ ...mapAcc, [shape.get('name')]: nodeId }),
    {},
  );
};

export class FlowchartSearch {
  /** The search value */
  value = '';
  /** The index of the focused result */
  index = 0;
  /** The number of matches in the graph for the given value */
  numMatches = 0;
  /** The highlighted shapes */
  shapes: Shape[] = [];
  /** The graph instance */
  private graph: Graph | null = null;
  private shapeNodeMap: ShapeNodeMap = {};

  constructor(graph: Graph, value = '') {
    this.graph = graph;
    this.execute(value);
  }

  /** Run the initial function */
  private execute(value = '') {
    const { graph } = this;
    this.clear();
    this.value = value;

    if (!graph || trim(value).length === 0) return;

    const { shapes, map } = graph.getNodes().reduce(
      (acc: { shapes: Shape[]; map: ShapeNodeMap }, node) => {
        const backgrounds = highlightTextInNode(node, value);
        return {
          shapes: [...acc.shapes, ...backgrounds],
          map: { ...acc.map, ...createShapeMap(node, backgrounds) },
        };
      },
      { shapes: [], map: {} },
    );

    this.shapes = shapes;
    this.numMatches = shapes.length;
    this.shapeNodeMap = map;
    this.goTo(0);

    graph.set(HIGHLIGHTED_SHAPES_GRAPH_KEY, shapes);
  }

  /** Go to the next result */
  public next() {
    const { index: previousIndex = 0, numMatches } = this;
    this.goTo(previousIndex + 1 < numMatches ? previousIndex + 1 : 0);
  }

  /** Go to the previous result */
  public prev() {
    const { index: previousIndex = 0, numMatches } = this;
    this.goTo(previousIndex - 1 < 0 ? numMatches - 1 : previousIndex - 1);
  }

  /** Changes the focus to a specific index */
  public goTo(index = 0) {
    const { index: previousIndex = 0, shapes = [], shapeNodeMap = {}, graph } = this;

    if (!graph) return;

    this.index = index;

    updateBackgroundColors(shapes, previousIndex, this.index);

    const currentNodeId = shapeNodeMap[shapes[index]?.get('name')];
    // TODO: only navigate if out of view
    focusNode(graph, currentNodeId, FocusPosition.UpperLeft, true, { duration: 150 });
  }

  /** Clear the graph */
  public clear() {
    this.numMatches = 0;
    this.value = '';
    this.shapes = [];
    this.shapeNodeMap = {};
    this.index = 0;

    if (this.graph) {
      removeHighlights(this.graph);
      this.graph.set(HIGHLIGHTED_SHAPES_GRAPH_KEY, []);
    }
  }
}

/** Search a node for a given string */
export const search = (graph: Graph, value = '') => new FlowchartSearch(graph, value);
